import { useReducer, useCallback } from 'react';


const BASE_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_DATA: 'SET_DATA',
  SET_ERROR: 'SET_ERROR',
  SET_PAGINATION: 'SET_PAGINATION',
  SET_FILTERS: 'SET_FILTERS',
  SET_MODE: 'SET_MODE',
  ADD_ITEM: 'ADD_ITEM',
  REMOVE_ITEM: 'REMOVE_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM'
};


const createBaseReducer = (customReducers = {}) => {
  const baseReducers = {
    [BASE_ACTIONS.SET_LOADING]: (state, action) => ({ ...state, loading: action.payload }),
    [BASE_ACTIONS.SET_DATA]: (state, action) => ({ ...state, data: action.payload }),
    [BASE_ACTIONS.SET_ERROR]: (state, action) => ({ ...state, error: action.payload }),
    [BASE_ACTIONS.SET_PAGINATION]: (state, action) => ({ ...state, pagination: action.payload }),
    [BASE_ACTIONS.SET_FILTERS]: (state, action) => ({ 
      ...state, 
      filters: { ...state.filters, ...action.payload } 
    }),
    [BASE_ACTIONS.SET_MODE]: (state, action) => ({ ...state, mode: action.payload }),
    [BASE_ACTIONS.ADD_ITEM]: (state, action) => ({ 
      ...state, 
      data: [action.payload, ...state.data],
      pagination: {
        ...state.pagination,
        totalItems: state.pagination.totalItems + 1
      }
    }),
    [BASE_ACTIONS.REMOVE_ITEM]: (state, action) => ({
      ...state,
      data: state.data.filter(item => item.uuid !== action.payload),
      pagination: {
        ...state.pagination,
        totalItems: state.pagination.totalItems - 1
      }
    }),
    [BASE_ACTIONS.UPDATE_ITEM]: (state, action) => ({
      ...state,
      data: state.data.map(item => 
        item.uuid === action.payload.uuid ? action.payload : item
      )
    }),
    ...customReducers
  };

  return (state, action) => {
    const reducer = baseReducers[action.type];
    return reducer ? reducer(state, action) : state;
  };
};


const useBaseContext = (initialState, loadDataService, filterDataFunction, dataKey, customReducers = {}) => {
  const [state, dispatch] = useReducer(createBaseReducer(customReducers), initialState);

  const loadItems = useCallback(async () => {
    try {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: BASE_ACTIONS.SET_ERROR, payload: null });
      
      const response = await loadDataService({
        tenantId: "demo-tenant",
        page: state.pagination.currentPage,
        pageSize: state.pagination.itemsPerPage
      });
      
      if (response.success) {
        dispatch({ type: BASE_ACTIONS.SET_DATA, payload: response.data[dataKey] });
        dispatch({ type: BASE_ACTIONS.SET_PAGINATION, payload: response.data.pagination });
      }
    } catch (error) {
      console.error(`Error loading ${dataKey}:`, error);
      dispatch({ type: BASE_ACTIONS.SET_ERROR, payload: error.message });
    } finally {
      dispatch({ type: BASE_ACTIONS.SET_LOADING, payload: false });
    }
  }, [loadDataService, state.pagination.currentPage, state.pagination.itemsPerPage, dataKey]);

  const updateFilters = useCallback((newFilters) => {
    dispatch({ type: BASE_ACTIONS.SET_FILTERS, payload: newFilters });
  }, []);

  const setMode = useCallback((mode) => {
    dispatch({ type: BASE_ACTIONS.SET_MODE, payload: mode });
  }, []);

  const setPage = useCallback((page) => {
    dispatch({ type: BASE_ACTIONS.SET_PAGINATION, payload: { ...state.pagination, currentPage: page } });
  }, [state.pagination]);
    
  const setItemsPerPage = useCallback((itemsPerPage) => {
    dispatch({ type: BASE_ACTIONS.SET_PAGINATION, payload: { ...state.pagination, itemsPerPage: itemsPerPage } });
  }, [state.pagination]);

  const filteredItems = useCallback(() => {
    return filterDataFunction(state.data, state.filters);
  }, [filterDataFunction, state.data, state.filters]);

  const reloadData = useCallback(() => {
    loadItems();
  }, [loadItems]);

  return {
    
    data: state.data,
    loading: state.loading,
    error: state.error,
    pagination: state.pagination,
    filters: state.filters,
    mode: state.mode,
    filteredItems: filteredItems(),
    
    loadItems,
    updateFilters,
    setMode,
    setPage,
    setItemsPerPage,
    reloadData,
    dispatch,
    
    ACTIONS: BASE_ACTIONS
  };
};

export { useBaseContext, BASE_ACTIONS }; 